import { OrderFulfillmentReducedFragment } from '@/graphql/fragments/orderFulfillmentReduced.generated';
import {
  EventAccount,
  ItemLocation,
  OrderFulfillmentFilter,
} from '@/graphql/generatedTypes';
import { useGetOrderFulfillmentsQuery } from '@/graphql/queries/getOrderFulfillments.generated';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { download, sanitizeFilename } from '@/utils/download';
import { LayoutStoreInstance } from '@/LayoutStore';
import { columns, Fulfillment } from './HandoutsReturnsTable/columns';
import { DataTable } from './HandoutsReturnsTable/DataTable';
import { Event } from '@/graphql/generatedTypes';
import ErrorBoundary from '@/utils/ErrorBoundary';
import { Button } from '@/components/ui/button';
import { Loader2Icon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { getMappedTransactionType } from './utils';

export const EventOwnerFulfillments = ({
  event,
}: {
  event: Event | undefined;
}) => {
  const { eventId } = useParams<{ eventId: string }>();

  const [isExporting, setIsExporting] = useState(false);

  const filter: OrderFulfillmentFilter = useMemo(
    () => ({
      event_id: eventId,
    }),
    [eventId],
  );

  const { data, loading, error } = useGetOrderFulfillmentsQuery({
    variables: {
      skip: 0,
      take: 1000,
      filter,
    },
    fetchPolicy: 'no-cache',
  });

  const filteredOrderFulfillments = useMemo(() => {
    if (!data) return [];
    return data.getOrderFulfillments.order_fulfillments.filter(
      (of) => of.is_billable,
    );
  }, [data]);

  const allItemLocations = useMemo(() => {
    const locs: Record<string, ItemLocation> = {};

    event?.event_participants
      ?.flatMap((eP) => {
        return eP?.item_locations || [];
      })
      .sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      })
      .forEach((l) => {
        locs[l.id] = l;
      });

    return Object.values(locs);
  }, [event]);

  const allEventAccounts = useMemo(() => {
    const eventAccounts: Record<string, EventAccount> = {};
    event?.event_participants?.forEach((e) => {
      eventAccounts[e.id] = e;
    });
    return Object.values(eventAccounts);
  }, [event]);

  // Helper function to get item count
  function getItemCount(orderFulfillment: OrderFulfillmentReducedFragment) {
    const containerStations = orderFulfillment.container_stations;
    const containersAmounts = containerStations
      .map(
        (cs) =>
          cs.containers.length +
          cs.manual_amounts.reduce(
            (acc, ma) => acc + ma.amount + ma.amount_sealed,
            0,
          ) +
          1,
      ) // +1 for the station itself
      .concat(orderFulfillment.goodwill.map((g) => g.amount + g.amount_sealed))
      .concat(
        orderFulfillment.manual_amounts.map((g) => g.amount + g.amount_sealed),
      );

    return containersAmounts.reduce(
      (a, b) => a + b,
      orderFulfillment.single_containers.length +
        orderFulfillment.assets.length,
    );
  }

  // Helper function to download CSV
  const downloadAsCSV = (data: any[], filename: string) => {
    const escapeCSV = (value: any): string => {
      const stringValue = String(value || '');
      // If value contains comma, quote, or newline, wrap it in quotes and escape internal quotes
      if (
        stringValue.includes(',') ||
        stringValue.includes('"') ||
        stringValue.includes('\n')
      ) {
        return '"' + stringValue.replace(/"/g, '""') + '"';
      }
      return stringValue;
    };

    const csvHeaders = Object.keys(data[0] || {})
      .map(escapeCSV)
      .join(',');
    const csvContent = data
      .map((row) => Object.values(row).map(escapeCSV).join(','))
      .join('\n');
    const fullCSV = csvHeaders + '\n' + csvContent;

    const blob = new Blob([fullCSV], { type: 'text/csv;charset=utf-8;' });
    download(blob, sanitizeFilename(filename) + '.csv');
  };

  async function downloadOrderFulfillments() {
    setIsExporting(true);
    try {
      // Use already filtered data for export
      if (filteredOrderFulfillments.length === 0) {
        LayoutStoreInstance.setError('No data to export');
        return;
      }

      // Transform data to match table columns
      const exportData = filteredOrderFulfillments.map((of) => ({
        [i18n.t('OrderFulfillment:eventAccount')]: of.event_account.name,
        [i18n.t('OrderFulfillment:type')]: getMappedTransactionType(of.type),
        [i18n.t('OrderFulfillment:itemLocation')]: of.item_location.name,
        [i18n.t('OrderFulfillment:itemCount')]: getItemCount(of),
        [i18n.t('OrderFulfillment:timestamp')]: new Date(
          of.timestamp,
        ).toLocaleDateString(i18n.language, getDateFormatOptions(true)),
      }));

      downloadAsCSV(
        exportData,
        `handouts_returns_${eventId}_${new Date().toISOString().split('T')[0]}`,
      );
    } catch (error) {
      console.error('Export error:', error);
      LayoutStoreInstance.setError('Error exporting handouts & returns');
    } finally {
      setTimeout(() => {
        setIsExporting(false);
      }, 1000);
    }
  }

  const tableData: Fulfillment[] = useMemo(
    () =>
      filteredOrderFulfillments.map((of) => ({
        id: of.id,
        event_account: of.event_account.name ?? of.event_account.id,
        item_location: of.item_location.name ?? of.item_location.id,
        item_count: getItemCount(of),
        timestamp: of.timestamp,
        type: of.type,
      })),
    [filteredOrderFulfillments],
  );

  const facetedFilters = useMemo(() => {
    if (!event) return [];
    return [
      {
        columnId: 'event_account',
        title: 'Event Account',
        options: allEventAccounts.map((e) => ({
          key: e.id,
          label: e.name ?? e.id,
          value: e.name ?? e.id,
        })),
      },
      {
        columnId: 'item_location',
        title: 'Item Location',
        options: allItemLocations.map((l) => ({
          key: l.id,
          label: l.name,
          value: l.name,
        })),
      },
      {
        columnId: 'type',
        title: 'Type',
        options: [
          { key: 'delivery', label: 'Delivery', value: 'delivery' },
          { key: 'return', label: 'Return', value: 'return' },
        ],
      },
    ];
  }, [event, allEventAccounts, allItemLocations]);

  if (error) {
    console.log(error);
    LayoutStoreInstance.setError(i18n.t('OrderFulfillment:errorOnLoad'));
  }

  return (
    <div className="tw:container tw:flex tw:flex-col tw:gap-4">
      <h1 className="tw:text-2xl tw:font-semibold">Handouts & Returns</h1>
      <ErrorBoundary>
        <DataTable
          key={eventId}
          isLoading={loading}
          error={error}
          columns={columns}
          data={tableData}
          opts={{
            mobileRowRepresentation: {
              type: 'cards',
              cardTitleColumnId: 'timestamp',
            },
            facetedFilters,
            ToolbarRightComponent: loading ? (
              <Skeleton className="tw:w-28 tw:h-8" />
            ) : (
              <Button
                disabled={isExporting}
                onClick={downloadOrderFulfillments}
                size="sm"
              >
                {isExporting && <Loader2Icon className="animate-spin" />}
                {i18n.t('Common:download')}
                {tableData.length > 0 && ` (${tableData.length})`}
              </Button>
            ),
          }}
          // renderMobileRow={(data) => {
          //   return (
          //     <Card>
          //       <CardHeader className="tw:pb-0">
          //         <span>
          //           {data.event_account} @ {data.item_location}
          //         </span>
          //       </CardHeader>
          //       <CardContent className="tw:py-2">
          //         <div className="tw:flex tw:flex-col tw:gap-2">
          //           <span className="tw:text-sm tw:text-gray-500">
          //             {data.type}
          //           </span>
          //           <span className="tw:text-sm tw:text-gray-500">
          //             {data.item_count} items
          //           </span>
          //         </div>
          //       </CardContent>
          //       <CardFooter>
          //         <span className="tw:text-sm tw:text-gray-500">
          //           {formatDate(data.timestamp)}, {formatTime(data.timestamp)}
          //         </span>
          //       </CardFooter>
          //     </Card>
          //   );
          // }}
        />
      </ErrorBoundary>
    </div>
  );
};
